import type { GatewayEndpoint } from "./types";
import {
  AddressHealth,
  GatewayMetaInfo,
  NO_GATEWAY_META,
  buildHubUrl,
  classifyHealth,
  parseGatewayMeta,
  reportedLatencyMs,
} from "./addresses";

/** A gateway address that does not answer its health check within this long is "unreachable". */
export const ADDRESS_PROBE_TIMEOUT_MS = 4000;
const META_TIMEOUT_MS = 4000;

const httpBase = (hubUrl: string) =>
  hubUrl
    .trim()
    .replace(/^wss:\/\//i, "https://")
    .replace(/^ws:\/\//i, "http://")
    .replace(/\/+$/, "");

/**
 * A gateway's address facts from its own `GET /api/meta`, cached per hub for the session (they
 * change only when the stack is redeployed). Never rejects: a gateway that is down, slow, or too
 * old to report the fields reads as "endpoints are not gateways" — the unchanged MT5-style flow.
 */
const cache = new Map<string, Promise<GatewayMetaInfo>>();

export function fetchGatewayMeta(
  hubUrl: string,
  { force = false }: { force?: boolean } = {}
): Promise<GatewayMetaInfo> {
  const base = httpBase(hubUrl);
  if (!force && cache.has(base)) return cache.get(base)!;

  let failed = false;
  const promise = (async () => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), META_TIMEOUT_MS);
    try {
      const res = await fetch(`${base}/api/meta`, { signal: ctrl.signal });
      if (!res.ok) throw new Error(`meta ${res.status}`);
      return parseGatewayMeta(await res.json());
    } catch {
      failed = true;
      return NO_GATEWAY_META;
    } finally {
      clearTimeout(timer);
    }
  })();

  cache.set(base, promise);
  // A failure is not a fact about the gateway — don't pin it for the session.
  void promise.then(() => {
    if (failed && cache.get(base) === promise) cache.delete(base);
  });
  return promise;
}

/** Test seam: forget every cached meta. */
export const clearGatewayMetaCache = () => cache.clear();

/** One `/healthz` request: what it answered and how long the answer took. Rejects when it fails. */
async function healthRequest(
  url: string,
  signal: AbortSignal
): Promise<{ ok: boolean; body: unknown; ms: number }> {
  const started = performance.now();
  const res = await fetch(url, { signal, cache: "no-store" });
  const ms = Math.max(0, Math.round(performance.now() - started));
  let body: unknown = null;
  try {
    // Reading the body to its end is also what frees the connection for the next request.
    body = await res.json();
  } catch {
    body = null; // answered, but not with JSON → "not ready"
  }
  return { ok: res.ok, body, ms };
}

/**
 * Ask one gateway address how it is: `GET {scheme}://host[:port]/healthz`. Resolves (never rejects)
 * with its state and the measured round trip.
 *
 * The address is asked TWICE, one request after the other. The first request to a host pays for
 * the DNS lookup and the TLS handshake, so it reads several times higher than the address really
 * is (production: 202 ms cold against 49 ms warm, same server); the second reuses the connection
 * and is the round trip a connected terminal will see. The state comes from the first answer; the
 * reported time is the second one, or the first when the second request fails. Both requests share
 * the one `timeoutMs` budget.
 */
export async function probeGatewayAddress(
  endpoint: GatewayEndpoint,
  scheme: "http" | "https" = "https",
  timeoutMs: number = ADDRESS_PROBE_TIMEOUT_MS
): Promise<AddressHealth> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const url = `${buildHubUrl(endpoint, scheme)}/healthz`;
  try {
    const first = await healthRequest(url, ctrl.signal);
    let warmMs: number | null = null;
    try {
      warmMs = (await healthRequest(url, ctrl.signal)).ms;
    } catch {
      warmMs = null; // the address already answered once — keep that answer and its time
    }
    return {
      state: classifyHealth({ ok: first.ok, body: first.body }),
      latencyMs: reportedLatencyMs(first.ms, warmMs),
    };
  } catch {
    return { state: classifyHealth(null) };
  } finally {
    clearTimeout(timer);
  }
}
